/** @jsx jsx */
import { jsx } from '@emotion/core'
import { VFlow } from 'bold-ui'
import { usePecField } from 'components/form'
import { LoadingIndicator } from 'components/loading'
import { PrivateRoute } from 'components/route'
import { useUltimoMChatQuery } from 'graphql/hooks.generated'
import { MChatRiscoEnum } from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { isEmpty, noop } from 'lodash'
import { useMemo, useRef } from 'react'
import { useRouteMatch } from 'react-router'
import { NIVEL_SUPERIOR } from 'types/NivelCbo'
import { calculateAge } from 'util/date/calculateAge'
import { MetaPath } from 'util/metaPath'

import { MChatAccordion } from '../components/MChatAccordion'
import { MChatModal } from '../components/MChatModal'
import { convertMChatRFQueryToModel } from '../converter-mchat'
import { MCHAT_URL_MODAL, MChatAccordionSectionEnum, MChatFieldModel, MChatRFModel } from '../model-mchat'
import { MChatFieldAccordionSection } from './MChatFieldAccordionSection'

interface MChatFieldProps {
  name: MetaPath<MChatRFModel>
}

export function MChatField(props: MChatFieldProps) {
  const { name } = props

  const match = useRouteMatch()
  const modalUrl = `${match.url}${MCHAT_URL_MODAL}`
  const ref = useRef<HTMLDivElement>()

  const {
    atendimentoProfissional: { id: atendProfId, iniciadoEm: dataAtendimento },
    cidadao: { id: cidadaoId, dataNascimento },
    prontuario: { id: prontuarioId },
  } = useAtendimentoContext()

  const {
    input: { value, onChange },
  } = usePecField({ name: name.mchatr })

  const handleSave = (values: MChatFieldModel) => {
    onChange(values)
  }

  const handleDelete = () => {
    onChange(null)
  }

  const handleClose = () => {
    setTimeout(() => ref?.current?.focus())
  }

  const {
    data: { ultimoMChatR },
    loading,
  } = useUltimoMChatQuery({
    variables: {
      prontuarioId,
    },
  })

  const valuesAtendimentoAnterior: MChatRFModel = useMemo(
    () => (loading ? null : convertMChatRFQueryToModel(ultimoMChatR)),
    [loading, ultimoMChatR]
  )

  const hasValuesAtendimentoAtual = !isEmpty(value)
  const hasValuesAntendimentoAnterior = !loading && !isEmpty(ultimoMChatR)
  const { ageInMonths } = calculateAge(dataNascimento, dataAtendimento.getTime())
  const { ageInMonths: ageInMonthsAtendimentoAnterior } =
    hasValuesAntendimentoAnterior && calculateAge(dataNascimento, ultimoMChatR.dataResultado)

  const isMChatAtualRiscoElevado = value?.risco === MChatRiscoEnum.ELEVADO
  const isMChatAnteriorRiscoElevado = valuesAtendimentoAnterior?.mchatr?.risco === MChatRiscoEnum.ELEVADO
  const hideConsultaSeguimento = hasValuesAtendimentoAtual ? isMChatAtualRiscoElevado : isMChatAnteriorRiscoElevado

  const checkIsPreExpanded = () => {
    if (ageInMonths >= 16 && !hasValuesAntendimentoAnterior) {
      return true
    }
    if (hasValuesAntendimentoAnterior && valuesAtendimentoAnterior?.mchatr?.risco === MChatRiscoEnum.BAIXO) {
      return !(ageInMonths < 24 || ageInMonthsAtendimentoAnterior > 24)
    }
    return false
  }

  if (loading) {
    return <LoadingIndicator message='Carregando dados do M-CHAT-R/F™...' />
  }

  return (
    <VFlow>
      <MChatAccordion isPreExpanded={checkIsPreExpanded()} isLoading={loading}>
        <div ref={ref} tabIndex={-1}>
          <VFlow vSpacing={0}>
            <MChatFieldAccordionSection
              name={name}
              section={MChatAccordionSectionEnum.MCHATR}
              valuesAtendimentoAnterior={valuesAtendimentoAnterior}
              atendimentoAnteriorAtendProfId={ultimoMChatR?.atendimentoProfissional?.id}
              modalUrl={modalUrl}
              onDelete={handleDelete}
              footerInfo={{
                dataResultado: ultimoMChatR?.dataResultado,
                lotacao: ultimoMChatR?.atendimentoProfissional?.lotacao,
                atendimentoProfissionalId: ultimoMChatR?.atendimentoProfissional?.id,
              }}
            />
            {!hideConsultaSeguimento && (
              <MChatFieldAccordionSection
                name={name}
                section={MChatAccordionSectionEnum.CONSULTA_SEGUIMENTO}
                valuesAtendimentoAnterior={valuesAtendimentoAnterior}
                atendimentoAnteriorAtendProfId={ultimoMChatR?.atendimentoProfissional?.id}
                modalUrl={null}
                onDelete={noop}
              />
            )}
          </VFlow>
        </div>
      </MChatAccordion>

      <PrivateRoute cbos={NIVEL_SUPERIOR} path={modalUrl} exact>
        <MChatModal
          name={name.mchatr}
          atendProfId={atendProfId}
          cidadaoId={cidadaoId}
          isEditing={false}
          onSave={handleSave}
          onClose={handleClose}
        />
      </PrivateRoute>

      <PrivateRoute cbos={NIVEL_SUPERIOR} path={`${modalUrl}/editar`} exact>
        <MChatModal
          name={name.mchatr}
          atendProfId={atendProfId}
          cidadaoId={cidadaoId}
          isEditing={true}
          onSave={handleSave}
          onClose={handleClose}
        />
      </PrivateRoute>
    </VFlow>
  )
}
